<script setup>
import { ref, watchEffect } from "vue";

const props = defineProps([
  "daySlots",
  "selectedDay",
  "selectedSlots",
  "selectedNumberOfReservations",
  "slotsNumber",
  'id',
  'name',
  'isValid'
]);
const emit = defineEmits(["change"]);

const isSlotAvailable = (slot) => {
  return (
    slot.slotStatus === "AVAILABLE" &&
    props.selectedNumberOfReservations <= slot.reservationsAvailable
  );
};

const isSlotIndexAvailable = (index) => {

  //Nota: index può valere sia lo slot precedente che successivo

  const slots = props.daySlots?.find(
    (slot) => slot.date === props.selectedDay
  )?.slots;

  if (indexes.value.has(index)) {
    // If the slot is already selected and if it is the first or the last selected slot
    return index === indexesMin.value || index === indexesMax.value;
  }

  if (availableSlots.value === 0) {
    // If there are no available slots
    return false;
  }

  if(index < indexesMin.value && indexesMin.value - index < 2) {
    const current = index + 1;
    const prev = index;

    if(current >= 0) {
      if(slots[prev].endTime !== slots[current].startTime) {
        return false;
      }
    }
  }

  if(index > indexesMax.value && index - indexesMax.value < 2) {
    const current = index - 1;
    const next = index;

    if(current >= 0) {
      if(slots[next].startTime !== slots[current].endTime) {
        return false;
      }
    }
  }

  // If the slot is at distance of 2 from selected slots
  return (
    indexesMin.value === -1 ||
    indexesMax.value === -1 ||
    (index < indexesMin.value && indexesMin.value - index < 2) ||
    (index > indexesMax.value && index - indexesMax.value < 2)
  );
};

const indexes = ref(new Set()); // The selected slots indexes
const indexesMin = ref(-1); // The minimum selected slot index
const indexesMax = ref(-1); // The maximum selected slot index
const availableSlots = ref(props.slotsNumber); // The available slots

const onSlotChange = (e, index) => {
  if (e.target.checked) {
    indexes.value.add(index);
  }
  if (!e.target.checked) {
    indexes.value.delete(index);
  }

  emit(
    "change",
    [...indexes.value]
      .sort((a, b) => a - b)
      .map((index) => {
        const slot = props.daySlots?.find(
          (slot) => slot.date === props.selectedDay
        )?.slots[index];
        return {
          startTime: slot.startTime,
          endTime: slot.endTime,
          slotDate: props.selectedDay,
        };
      })
  );
};

// Updates the indexes when the selected slots change
watchEffect(async () => {
  const newIndexes = new Set(
    (props.selectedSlots || [])
      .map((selectedSlot) =>
        props.daySlots
          ?.find((slot) => slot.date === props.selectedDay)
          ?.slots.findIndex((slot) => selectedSlot.startTime === slot.startTime)
      )
      .filter((index) => index !== -1)
  );
  indexes.value = newIndexes;
});

watchEffect(async () => {
  availableSlots.value = props.slotsNumber - indexes.value.size;
  indexesMin.value = indexes.value.size
    ? Math.min.apply(this, [...indexes.value])
    : -1;
  indexesMax.value = indexes.value.size
    ? Math.max.apply(this, [...indexes.value])
    : -1;
});
</script>
<style scoped>
#hoursList {
  position: relative;
}
@media screen and (min-width: 992px) {
  #hoursList {
    height: 420px;
    overflow-y: auto;
  }
}
</style>
<template>
<div :id="`${props.id}`">
  <div aria-live="polite" class="visually-hidden">
    <label for="hoursList" tabindex="0">Elenco degli orari prenotabili nel giorno {{ (new Date(selectedDay)).toLocaleString('it-IT', { weekday: 'long', year: "numeric", month: "long", day: "numeric" }) }}</label>
  </div>
  <div class="bg-white p-4" id="hoursList">
    <div
      v-for="(slot, index) in props.daySlots?.find(
        (slot) => slot.date === props.selectedDay
      )?.slots || []"
      :key="slot.startTime"
    >
      <div class="form-check form-check-group">
        <input
          :name="`${props.name}`"
          :id="`${props.id}_${index}`"
          :aria-label="`Orario ${slot.startTime} - Posti disponibili (${slot.reservationsAvailable})`"
          :checked="indexes.has(index)"
          type="checkbox"
          :disabled="!isSlotAvailable(slot) || !isSlotIndexAvailable(index)"
          data-yup-label="Orario"
          :data-yup-error-id-label="`hoursList`"
          :data-yup-error-id-field="`hoursList`"
          @change="
            (e) => {
              onSlotChange(e, index);
            }
          "
        />
        <label
          :for="`${props.id}_${index}`"
          :class="`${
            slot.slotStatus !== 'AVAILABLE' ||
            selectedNumberOfReservations > slot.reservationsAvailable
              ? 'text-muted'
              : ''
          }`"
        >
          {{ slot.startTime }} ({{ slot.reservationsAvailable }})
          <br v-if="!isSlotAvailable(slot)" />
          <small class="text-muted" v-if="!isSlotAvailable(slot)"
          >Esaurito</small
          >
        </label>
      </div>
    </div>
  </div>
</div>
</template>
